<script>
	/**
	 * This component creates a row of share buttons for Twitter, Pinterest & Email
	 * (with Edmodo and Facebook support tentatively on the horizon). It works by
	 * creating share buttons that share the current URL of the page along with a
	 * title and thumbnail which are set using attributes.
	 *
	 * Caveat: the email body is still largely hard coded in the postToEmail method.
	 *
	 * Example:
	 *
	 * <share-buttons
	 *   share-title="Look at this"
	 *   share-thumbnail="http://example.com/some/image.jpg">
	 * </share-buttons>
	 *
	 * Attributes:
	 *
	 * share-thumbnail - a URL to a thumbnail for the share (e.g. article.thumbnail)
	 *
	 * share-title - the default text for the user to share the page
	 *
	 * no-querystring - (optional) if present then don't include the querystring in the share URL
	 *
	 */

	var routeContext = require('core/routeContext');
	const _ = require('underscore');
	const model = require('model/model');

	const analyticsUtilities = require('utilities/analyticsUtilities');
	const fireEvent = analyticsUtilities.fireEvent;
	const SOCIAL_MEDIA_SHARE = analyticsUtilities.events.SOCIAL_MEDIA_SHARE;

	require('social/pinterestapi');

	var FACEBOOK_APP_ID = '259776127711828';

	module.exports = {
		props: {
			shareThumbnail: {
				type: String,
				default: ''
			},
			shareTitle: {
				type: String,
				default: 'ReadWorks'
			},
			noQuerystring: {
				type: Boolean,
				default: false
			},
			glyphs: {
				type: Boolean,
				default: false
			},
			eventData: {
				type: Object,
				default: {}
			}
		},
		modelData: ['gcAddon'],
		data() {
			return {
				siteName: 'ReadWorks',
				url: 'https://www.readworks.org/',
				shareTitle: 'share title',
				text: 'Check out this article on ReadWorks'
			};
		},
		mounted() {
			var self = this;
			self.url = self.getFullUrl();
			this.$subscribe('route-changing', function () {
				self.url = self.getFullUrl();
			});
		},
		methods: {
			getFullUrl() {
				return location.protocol + '//www.readworks.org' + (this.noQuerystring ? routeContext.pathname : routeContext.path);
			},
			share(url, thisWindow) {
				if (thisWindow) {
					location.href = url;
				} else {
					window.open(url, 'sharer');
				}
			},
			shareEvent(data) {
				let eventData = this.eventData;
				let activeProduct = model.get('activeProduct');
				let pid = activeProduct ? activeProduct.uuid : undefined;

				data = _.assign(
					{
						pid
					},
					data,
					eventData
				);

				fireEvent(SOCIAL_MEDIA_SHARE, data);
			},
			postToFacebook() {
				this.share(
					'https://www.facebook.com/dialog/share?app_id=' +
						FACEBOOK_APP_ID +
						'&display=popup&href=' +
						encodeURIComponent(this.getFullUrl()) +
						'&redirect_uri=' +
						encodeURIComponent('https://www.readworks.org')
				);
				this.shareEvent({
					platform: 'facebook'
				});
			},
			/**
			 * Post to twitter (only used as a fallback when the Tweet widget fails to load)
			 * @return {[type]} [description]
			 */
			postToTwitter(event) {
				event.preventDefault();
				var url = this.getFullUrl();
				this.share('http://twitter.com/intent/tweet?text=' + encodeURIComponent(this.shareTitle) + '&url=' + encodeURIComponent(url) + '&via=ReadWorks');
				this.shareEvent({
					platform: 'twitter'
				});
			},
			/* eslint-disable no-undef */
			postToPinterest() {
				var url = this.getFullUrl();
				if (window.PinUtils) {
					PinUtils.pinOne({
						url: url,
						media: this.shareThumbnail,
						description: this.shareTitle
					});
				} else {
					this.share(
						'http://pinterest.com/pin/create/button?url=' +
							encodeURIComponent(url) +
							'&media=' +
							encodeURIComponent(this.shareThumbnail) +
							'&description=' +
							encodeURIComponent(this.shareTitle)
					);
				}
				this.shareEvent({
					platform: 'pinterest'
				});
			},
			/* eslint-enable no-undef */
			postToEmail() {
				var url = this.getFullUrl();
				var body =
					'You should check out ReadWorks. I love their research-based reading ' +
					'comprehension curriculum, and you might too. ReadWorks is completely free—all' +
					' you have to do is register!\n\n' +
					this.shareTitle +
					'\n' +
					url;
				this.share('mailto:?subject=' + encodeURIComponent(this.shareTitle) + '&body=' + encodeURIComponent(body), true);
				this.shareEvent({
					platform: 'email'
				});
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'components/buttons';

	.share-buttons {
		&:not(.share-glyphs) .button-share {
			@extend %btn;
			padding: 0.75em;
			color: $c-white;
			text-decoration: none;
			font-size: 0.8125em;
			font-weight: 700;
			line-height: 2.05em;
			background-color: $c-slate-dark;
			border-radius: 3px;
			padding: 0 0.7em;

			&.edmodo {
				background-color: $c-edmodo;
			}

			&.facebook {
				background-color: $c-facebook;
			}

			&.twitter {
				background-color: $c-twitter;
			}

			&.pinterest {
				background-color: $c-pinterest;
			}

			i {
				font-size: 1em;
				line-height: 2.05em;
				margin-right: 0.1em;
				vertical-align: middle;
			}
		}

		&.share-glyphs {
			.share-glyph {
				height: 2rem;
				width: 2rem;
				text-align: center;
				border-radius: 1rem;
				background-color: transparent;
				border-style: solid;
				border-width: 1px;
				padding: 0;

				&.edmodo {
					border-color: $c-edmodo;
					color: $c-edmodo;

					&:hover {
						box-shadow: $c-edmodo 1px 1px 4px;
					}
				}

				&.facebook {
					border-color: $c-facebook;
					color: $c-facebook;

					&:hover {
						box-shadow: $c-facebook 1px 1px 4px;
					}
				}

				&.twitter {
					border-color: $c-twitter;
					color: $c-twitter;

					&:hover {
						box-shadow: $c-twitter 1px 1px 4px;
					}
				}

				&.pinterest {
					border-color: $c-pinterest;
					color: $c-pinterest;

					&:hover {
						box-shadow: $c-pinterest 1px 1px 4px;
					}
				}

				&.email {
					border-color: $c-slate;
					color: $c-slate-dark;

					&:hover {
						box-shadow: $c-slate 1px 1px 4px;
					}
				}
			}
		}
	}
</style>
<template>
	<!--
FIXME: below is a temporary fix we need to check shareTitle to avoid rendering
too early. When article.vue initializes the article is {} which causes the non-paired
text template to render even if this is a paired text. So the share buttons are visible
for an instant then disappear. I think we need to initialize article to null, but this
may have other repercussions. For now checking shareTitle solves the issue.
-->
	<div
		class="share-buttons"
		:class="{
			'share-glyphs': glyphs
		}"
		v-if="shareTitle && !gcAddon">
		<template v-if="!glyphs">
			<!-- <li><a href="#" class="button-share edmodo">Edmodo</a></li>-->
			<button
				@click="postToPinterest()"
				class="button-share pinterest"
				aria-label="Post to Pinterest">
				<img
					width="34"
					aria-hidden="true"
					src="data:image/svg+xml;base64,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" />
			</button>
			<span
				class="tweet-button-container"
				ref="tweetButtonContainer">
				<a
					href="https://twitter.com/intent/tweet"
					class="twitter-share-button button-share twitter"
					ref="fallbackTweetButton"
					:data-text="shareTitle"
					:data-url="url"
					data-size="large"
					data-via="ReadWorks"
					@click="postToTwitter">
					<i
						class="icon-twitter"
						aria-hidden="true"></i>
					Tweet
				</a>
			</span>
			<button
				@click="postToFacebook"
				class="button-share facebook">
				<i
					class="icon-facebook"
					aria-label="Facebook"></i>
				&nbsp;Share
			</button>
			<button
				@click="postToEmail()"
				class="button-share">
				<i
					class="icon-mail"
					aria-hidden="true"></i>
				Email
			</button>
		</template>
		<template v-if="glyphs">
			<button
				class="share-glyph pinterest"
				@click="postToPinterest()"
				aria-label="Post to Pinterest">
				<i
					class="fa-brands fa-pinterest-p"
					aria-hidden="true"></i>
			</button>
			<button
				class="share-glyph facebook"
				@click="postToFacebook"
				aria-label="Share on Facebook">
				<i
					class="fa-brands fa-facebook-f"
					aria-hidden="true"></i>
			</button>
			<button
				class="share-glyph twitter"
				@click="postToTwitter"
				aria-label="Tweet">
				<i
					class="fa-brands fa-twitter"
					aria-hidden="true"></i>
			</button>
			<button
				class="share-glyph email"
				@click="postToEmail()"
				aria-label="Share via email">
				<i
					class="fa-solid fa-envelope"
					aria-hidden="true"></i>
			</button>
		</template>
	</div>
</template>
